
import { NavigateFunction } from "react-router-dom";

// Define the possible auth flow types
export type AuthFlowType = 'signup' | 'recovery' | 'invite' | 'signin' | null;

// Define the possible auth flow stages as an enum for better type safety
export enum AuthFlowStage {
  STARTED = 'started',
  EMAIL_SENT = 'email_sent',
  TOKEN_VERIFIED = 'token_verified',
  SESSION_CREATED = 'session_created',
  PROFILE_CHECKED = 'profile_checked',
  COMPLETED = 'completed',
  ERROR = 'error'
}

export interface AuthFlowState {
  type: AuthFlowType;
  stage: AuthFlowStage;
  email?: string;
  timestamp: string;
  userId?: string;
  profileExists?: boolean;
  token?: string;
  isCompleted?: boolean;
}

export class AuthNavigationController {
  private static instance: AuthNavigationController;
  private navigate: NavigateFunction | null = null;
  private currentState: AuthFlowState = {
    type: null,
    stage: AuthFlowStage.STARTED,
    timestamp: new Date().toISOString()
  };

  // Private constructor for singleton pattern
  private constructor() {
    // Initialize from localStorage if available
    this.loadState();
  }

  // Singleton accessor
  public static getInstance(): AuthNavigationController {
    if (!AuthNavigationController.instance) {
      AuthNavigationController.instance = new AuthNavigationController();
    }
    return AuthNavigationController.instance;
  }

  // Set the navigate function for redirects
  public setNavigate(navigate: NavigateFunction): void {
    this.navigate = navigate;
  }

  // Start a new auth flow
  public startFlow(type: AuthFlowType, email?: string, token?: string): void {
    console.log(`[AuthNavigation] Starting ${type} flow${email ? ` for ${email}` : ''}${token ? ' with token' : ''}`);
    
    this.currentState = {
      type,
      stage: AuthFlowStage.STARTED,
      email,
      token,
      timestamp: new Date().toISOString(),
      isCompleted: false
    };
    
    this.saveState();
  }

  // Update the current auth flow stage
  public updateStage(stage: AuthFlowStage, additionalData?: Partial<AuthFlowState>): void {
    if (!this.currentState.type) {
      console.warn('[AuthNavigation] Attempting to update stage without an active flow');
      return;
    }
    
    console.log(`[AuthNavigation] Updating ${this.currentState.type} flow to stage: ${stage}`);
    
    this.currentState = {
      ...this.currentState,
      ...additionalData,
      stage,
      timestamp: new Date().toISOString()
    };
    
    this.saveState();
  }

  // Complete the current auth flow and perform the appropriate redirect
  public completeFlow(success: boolean, userId?: string): void {
    if (!this.currentState.type) {
      console.warn('[AuthNavigation] Attempting to complete flow without an active flow');
      return;
    }
    
    if (!this.navigate) {
      console.error('[AuthNavigation] Navigate function not set, cannot redirect');
      return;
    }
    
    console.log(`[AuthNavigation] Completing ${this.currentState.type} flow. Success: ${success}`);
    
    this.currentState.stage = success ? AuthFlowStage.COMPLETED : AuthFlowStage.ERROR;
    this.currentState.userId = userId;
    this.currentState.isCompleted = true;
    this.saveState();
    
    // Handle redirects based on flow type and success state
    if (success) {
      this.handleSuccessRedirect();
    } else {
      this.handleErrorRedirect();
    }
  }

  // Perform redirects based on the successful completion of different auth flows
  private handleSuccessRedirect(): void {
    if (!this.navigate) {
      console.error('[AuthNavigation] Navigate function not set, cannot redirect');
      return;
    }
    
    const { type } = this.currentState;
    
    console.log('[AuthNavigation] Flow type in handleSuccessRedirect:', type);
    
    // Apply business rules for redirects based on flow type
    switch (type) {
      case 'signup':
        // Rule: After signup verification, redirect to terms of service
        console.log('[AuthNavigation] Redirecting to terms of service after signup');
        this.navigate('/terms-of-service');
        break;
        
      case 'recovery':
        // Check if we need to redirect to the reset password page or have already reset the password
        if (this.currentState.isCompleted) {
          // If password reset is already completed, go to home
          console.log('[AuthNavigation] Password reset completed, redirecting to home');
          // Clear recovery flow flags
          localStorage.removeItem('lamulamu_in_recovery');
          this.navigate('/');
        } else {
          // Rule: After password reset verification, redirect to reset password page
          console.log('[AuthNavigation] Redirecting to reset password page after recovery verification');
          // If we have a token, include it in the URL
          if (this.currentState.token) {
            this.navigate(`/auth?view=resetPassword&token=${this.currentState.token}&type=recovery`);
          } else {
            this.navigate('/auth?view=resetPassword&type=recovery');
          }
        }
        break;
        
      case 'invite':
        // Rule: After invite, user is not signed in, redirect to create password page
        console.log('[AuthNavigation] Redirecting to auth page for invite completion');
        this.navigate('/auth?view=createPassword');
        break;
        
      case 'signin':
        // Rule: After normal signin, redirect to home or originally requested page
        console.log('[AuthNavigation] Redirecting to home after signin');
        this.navigate('/');
        break;
        
      default:
        // Default redirect to home
        console.log('[AuthNavigation] No specific redirect rule, going to home');
        this.navigate('/');
    }
  }

  // Handle redirects for error cases
  private handleErrorRedirect(): void {
    if (!this.navigate) {
      console.error('[AuthNavigation] Navigate function not set, cannot redirect');
      return;
    }
    
    console.log('[AuthNavigation] Redirecting to auth page due to error');
    this.navigate('/auth');
    
    // Don't clear state on error to allow debugging
  }

  // Force save the state immediately to localStorage
  public forceSaveState(): void {
    try {
      console.log('[AuthNavigation] Force saving state:', this.currentState);
      
      // Save the flow type for backward compatibility
      if (this.currentState.type) {
        localStorage.setItem('lamulamu_auth_flow_type', this.currentState.type);
        localStorage.setItem('lamulamu_auth_flow_timestamp', this.currentState.timestamp);
      } else {
        localStorage.removeItem('lamulamu_auth_flow_type');
        localStorage.removeItem('lamulamu_auth_flow_timestamp');
      }
      
      // Save the full state for the controller
      localStorage.setItem('lamulamu_auth_flow_state', JSON.stringify(this.currentState));
    } catch (e) {
      console.error('[AuthNavigation] Error force saving state to localStorage:', e);
    }
  }

  // Save the current auth flow state to localStorage
  private saveState(): void {
    try {
      // Save the flow type for backward compatibility
      if (this.currentState.type) {
        localStorage.setItem('lamulamu_auth_flow_type', this.currentState.type);
        localStorage.setItem('lamulamu_auth_flow_timestamp', this.currentState.timestamp);
      } else {
        localStorage.removeItem('lamulamu_auth_flow_type');
        localStorage.removeItem('lamulamu_auth_flow_timestamp');
      }
      
      // Save the full state for the controller
      localStorage.setItem('lamulamu_auth_flow_state', JSON.stringify(this.currentState));
    } catch (e) {
      console.error('[AuthNavigation] Error saving state to localStorage:', e);
    }
  }

  // Load the auth flow state from localStorage
  private loadState(): void {
    try {
      // Try to load the full state first
      const stateJson = localStorage.getItem('lamulamu_auth_flow_state');
      if (stateJson) {
        const parsedState = JSON.parse(stateJson);
        // Convert string stage to enum if needed
        if (typeof parsedState.stage === 'string') {
          // Find the matching enum value
          const enumStage = Object.values(AuthFlowStage).find(
            value => value === parsedState.stage
          );
          if (enumStage) {
            parsedState.stage = enumStage;
          } else {
            // Default to STARTED if the stage is not recognized
            parsedState.stage = AuthFlowStage.STARTED;
          }
        }
        this.currentState = parsedState;
        return;
      }
      
      // Fall back to legacy storage format
      const type = localStorage.getItem('lamulamu_auth_flow_type') as AuthFlowType;
      const timestamp = localStorage.getItem('lamulamu_auth_flow_timestamp') || new Date().toISOString();
      
      if (type) {
        this.currentState = {
          type,
          stage: AuthFlowStage.STARTED,
          timestamp
        };
      }
    } catch (e) {
      console.error('[AuthNavigation] Error loading state from localStorage:', e);
      this.clearState();
    }
  }

  // Clear the auth flow state
  public clearState(): void {
    this.currentState = {
      type: null,
      stage: AuthFlowStage.STARTED,
      timestamp: new Date().toISOString()
    };
    
    try {
      localStorage.removeItem('lamulamu_auth_flow_type');
      localStorage.removeItem('lamulamu_auth_flow_timestamp');
      localStorage.removeItem('lamulamu_auth_flow_state');
      localStorage.removeItem('lamulamu_in_recovery');
    } catch (e) {
      console.error('[AuthNavigation] Error clearing state from localStorage:', e);
    }
  }

  // Get the current auth flow state (for debugging and display)
  public getState(): AuthFlowState {
    return { ...this.currentState };
  }
}

// Export a singleton instance
export const authNavigationController = AuthNavigationController.getInstance();
